import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../lib/backend';

const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  // Server-verified role flags. NEVER derived from `user.role` in the client
  // (that can be tampered with in the login response). Each is flipped true
  // only after the corresponding `/verify-role` endpoint returns 204 — which
  // requires a signed JWT cookie that resolves to that role server-side.
  const [adminVerified, setAdminVerified] = useState(false);
  const [artistVerified, setArtistVerified] = useState(false);
  const [agentVerified, setAgentVerified] = useState(false);
  const [adminVerifying, setAdminVerifying] = useState(false);

  const verifyRoles = async (candidate) => {
    // Skip the network call entirely for plain buyers — they never trigger
    // any role-gated UI, so nothing to verify.
    const claim = candidate?.role;
    const inTestMode = !!candidate?.test_mode?.active;
    const anyPrivileged = claim === 'admin' || claim === 'artist' || claim === 'agent' || inTestMode;
    if (!anyPrivileged) {
      setAdminVerified(false);
      setArtistVerified(false);
      setAgentVerified(false);
      return;
    }

    setAdminVerifying(true);
    try {
      const res = await axios.get(`${API}/auth/verify-roles`, { withCredentials: true });
      setAdminVerified(!!res.data?.is_admin);
      setArtistVerified(!!res.data?.is_artist);
      setAgentVerified(!!res.data?.is_agent);
    } catch {
      // 401 or network error → treat all as unverified (safe default)
      setAdminVerified(false);
      setArtistVerified(false);
      setAgentVerified(false);
    } finally {
      setAdminVerifying(false);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Cookie is sent automatically by axios (withCredentials)
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        setToken('cookie');  // Signal that user is authenticated
        await verifyRoles(response.data);
      } catch {
        setToken(null);
        setUser(null);
        setAdminVerified(false);
        setArtistVerified(false);
        setAgentVerified(false);
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    // Admin 2FA: server withholds the cookie and returns a pending token instead.
    if (response.data?.requires_totp) {
      return { requires_totp: true, totp_pending_token: response.data.totp_pending_token };
    }
    const { user: userData } = response.data;
    setToken('cookie');
    setUser(userData);
    await verifyRoles(userData);
    return userData;
  };

  const verifyTotp = async (totpPendingToken, code) => {
    const response = await axios.post(
      `${API}/auth/totp/verify`,
      { totp_pending_token: totpPendingToken, code },
      { withCredentials: true }
    );
    const { user: userData } = response.data;
    setToken('cookie');
    setUser(userData);
    await verifyRoles(userData);
    return userData;
  };

  const register = async (email, password, name, role = 'user') => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role }, { withCredentials: true });
    const { user: userData } = response.data;
    setToken('cookie');
    setUser(userData);
    await verifyRoles(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      console.error('Logout request failed');
    }
    setToken(null);
    setUser(null);
    setAdminVerified(false);
    setArtistVerified(false);
    setAgentVerified(false);
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      await verifyRoles(response.data);
    } catch {
      console.error('Failed to refresh user');
    }
  };

  const resendVerification = async () => {
    const response = await axios.post(`${API}/auth/resend-verification`, {}, { withCredentials: true });
    return response.data;
  };

  const value = {
    user,
    setUser,
    token,
    loading,
    login,
    verifyTotp,
    register,
    logout,
    refreshUser,
    resendVerification,
    isAuthenticated: !!user,
    // All role flags are server-verified — client-tampered `user.role` from
    // the login response cannot trigger role-gated UI on any of them.
    isAdmin: adminVerified,
    isArtist: artistVerified,
    isAgent: agentVerified,
    adminVerifying,
    isVerified: user?.email_verified || adminVerified || artistVerified || agentVerified || !!user?.test_mode?.active,
    testMode: user?.test_mode || { active: false },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
